import { registerPushSubscription, unregisterPushSubscription } from '../../graphql/mutations';
import { queryGqlApi } from '../../graphql/queryGqlApi';
import { CrudServices } from '../../platform/services/types/ServiceDependencies';

const DEFAULT_PROMPT_KEY = 'push-notification-permission-requested-v1';

function urlBase64ToUint8Array(base64String: string) {
    const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let index = 0; index < rawData.length; index += 1) {
        outputArray[index] = rawData.charCodeAt(index);
    }

    return outputArray;
}

export function isPushNotificationsSupported() {
    return typeof window !== 'undefined'
        && 'serviceWorker' in navigator
        && 'PushManager' in window
        && 'Notification' in window;
}

async function getCurrentBrowserSubscription() {
    if (!isPushNotificationsSupported()) {
        return null;
    }

    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) {
        return null;
    }

    return registration.pushManager.getSubscription();
}

export async function syncBrowserPushSubscription(
    services: CrudServices,
    {
        promptIfNeeded = false,
        promptStorageKey = DEFAULT_PROMPT_KEY,
    }: {
        promptIfNeeded?: boolean;
        promptStorageKey?: string;
    } = {}
) {
    if (!isPushNotificationsSupported()) {
        return false;
    }

    const vapidPublicKey = import.meta.env.VITE_APP_VAPID_PUBLIC_KEY;
    if (!vapidPublicKey) {
        return false;
    }

    let permission = Notification.permission;
    if (permission === 'default' && promptIfNeeded && !localStorage.getItem(promptStorageKey)) {
        localStorage.setItem(promptStorageKey, new Date().toISOString());
        permission = await Notification.requestPermission();
    }

    if (permission !== 'granted') {
        await unregisterBrowserPushSubscription(services, {
            unsubscribeBrowser: permission === 'denied',
        });
        return false;
    }

    const registration = await navigator.serviceWorker.ready;
    let subscription = await registration.pushManager.getSubscription();

    if (!subscription) {
        subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
        });
    }

    const subscriptionJson = subscription.toJSON();
    const keys = subscriptionJson.keys;
    if (!keys?.p256dh || !keys.auth) {
        return false;
    }

    await queryGqlApi(
        registerPushSubscription,
        {
            input: {
                endpoint: subscription.endpoint,
                keys: {
                    p256dh: keys.p256dh,
                    auth: keys.auth,
                },
                userAgent: navigator.userAgent,
            },
        },
        services.dataClientService,
        services.authService,
        'pgServer'
    );

    return true;
}

export async function unregisterBrowserPushSubscription(
    services: CrudServices,
    {
        unsubscribeBrowser = false,
    }: {
        unsubscribeBrowser?: boolean;
    } = {}
) {
    const subscription = await getCurrentBrowserSubscription();
    if (!subscription) {
        return false;
    }

    try {
        await queryGqlApi(
            unregisterPushSubscription,
            {
                input: {
                    endpoint: subscription.endpoint,
                },
            },
            services.dataClientService,
            services.authService,
            'pgServer'
        );
    } catch (error) {
        console.warn('Failed to unregister push subscription on the server', error);
    }

    if (unsubscribeBrowser) {
        try {
            await subscription.unsubscribe();
        } catch (error) {
            console.warn('Failed to unsubscribe browser push subscription', error);
        }
    }

    return true;
}
